/**
 * 选区相关的纯函数：从编辑器状态里提取选中的内容 / 标题信息
 */

/**
 * 提取选中内容（文本、公式、图片、表格），并收集涉及的 blockId
 */
export const extractSelectedContent = (editor) => {
    const { from, to } = editor.state.selection;
    const { doc } = editor.state;
    let content = '';
    let inTableCell = false;
    let cellContent = '';
    let isFirstCellInRow = true;
    const blockIds = new Set();

    doc.nodesBetween(from, to, (node, pos) => {
        if (node.attrs && node.attrs.blockId) {
            blockIds.add(node.attrs.blockId);
        }

        if (node.isText) {
            const start = Math.max(from, pos) - pos;
            const end = Math.min(to, pos + node.nodeSize) - pos;
            const text = node.text.substring(start, end);
            if (inTableCell) cellContent += text;
            else content += text;
        } else if (node.type.name === 'inlineMath') {
            const mathText = `[公式#${node.attrs.blockId}:${node.attrs.latex || ''}]`;
            if (inTableCell) cellContent += mathText;
            else content += mathText;
        } else if (node.type.name === 'blockMath') {
            content += `\n[公式块#${node.attrs.blockId}:${node.attrs.latex || ''}]\n`;
        } else if (node.type.name === 'image') {
            content += `\n[图片#${node.attrs.blockId}: ${node.attrs.src || ''}]\n`;
        } else if (node.type.name === 'tableRow') {
            if (!isFirstCellInRow) content += '\n';
            isFirstCellInRow = true;
        } else if (node.type.name === 'tableHeader' || node.type.name === 'tableCell') {
            inTableCell = true;
            cellContent = '';
        } else if (node.type.name === 'paragraph') {
            const parent = doc.resolve(pos).parent;
            const blockId = node.attrs.blockId;
            if (parent.type.name === 'tableCell' || parent.type.name === 'tableHeader') {
                content += cellContent;
                if (pos + node.nodeSize <= to) {
                    content += ' | ';
                    isFirstCellInRow = false;
                }
                cellContent = '';
                inTableCell = false;
            } else {
                if (blockId && content && !content.endsWith('\n')) {
                    content += ` [段落#${blockId}]`;
                }
                if (content && !content.endsWith('\n')) content += '\n';
            }
        } else if (node.type.name === 'heading') {
            const level = node.attrs.level || 1;
            content += `\n${'#'.repeat(level)} [标题#${node.attrs.blockId}] `;
        } else if (node.type.name === 'listItem') {
            content += '\n- ';
        }
        return true;
    });

    if (cellContent) content += cellContent;

    content = content.replace(/\| \n/g, '|\n').replace(/\n\n+/g, '\n\n').trim();
    return { content, blockIds: Array.from(blockIds) };
};

/**
 * 判断当前选区是否落在标题上（光标在标题内，或选区只包含标题）
 */
export const getHeadingInfo = (editor) => {
    const { from, to } = editor.state.selection;
    const { doc } = editor.state;

    const build = (node, pos) => ({
        isHeading: true,
        node,
        pos,
        level: node.attrs.level,
        detailId: node.attrs.detailId,
        blockId: node.attrs.blockId,
    });

    if (from === to) {
        const $pos = doc.resolve(from);
        for (let depth = $pos.depth; depth >= 0; depth -= 1) {
            const node = $pos.node(depth);
            if (node.type.name === 'heading') {
                return build(node, $pos.before(depth));
            }
        }
        return { isHeading: false };
    }

    let headingNode = null;
    let headingPos = null;
    let onlyHeading = true;
    doc.nodesBetween(from, to, (node, pos) => {
        if (node.type.name === 'heading') {
            headingNode = node;
            headingPos = pos;
        } else if (node.isTextblock) {
            onlyHeading = false;
        }
    });

    if (headingNode && onlyHeading) return build(headingNode, headingPos);
    return { isHeading: false };
};

/**
 * 当前选区是否处于受保护内容（参考文献）里
 */
export const isSelectionInProtectedContent = (editor) => {
    const { from } = editor.state.selection;
    const $from = editor.state.doc.resolve(from);
    for (let depth = $from.depth; depth >= 0; depth -= 1) {
        if ($from.node(depth).attrs?.isProtected === true) return true;
    }
    return false;
};

/**
 * 光标前最近的一个可改稿标题（用于顶栏「改稿」按钮的默认对象）
 */
export const findNearestRewritableHeading = (editor) => {
    if (!editor) return null;
    const { from } = editor.state.selection;
    let found = null;
    editor.state.doc.nodesBetween(0, Math.max(from, 0), (node, pos) => {
        if (node.type.name === 'heading' && pos <= from) {
            const { attrs } = node;
            if (attrs.isLowestChapter && !attrs.isProtected && attrs.detailId) {
                found = { node, pos };
            }
        }
        return node.type.name !== 'heading';
    });
    return found;
};
